import { MDX } from "./mdx/tokens";
import { LANGS } from "./mdx/tokens";
import { useTheme } from "@/context/ThemeProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DISCLAIMER, resolveLang } from "@/components/BeCoDisclaimerOverlay";

/**
 * ComplianceFooter — 4-column footer.
 *
 *  Col 1: Product           → in-page section anchors
 *  Col 2: Company           → About · FAQ · Contact
 *  Col 3: Compliance Docs   → Terms · Privacy · Cookies · Disclaimer · Risk
 *  Col 4: Corporate Info +
 *         full disclaimer   → strict-overwrite legal copy (unchanged)
 *
 * The disclaimer copy stays verbatim in Col 4 — it is the compliance surface
 * area. No M-Pesa Paybill numbers, no account numbers, no CMA notices.
 *
 * `isDark` may be passed explicitly (Landing page) or auto-derived from the
 * ThemeProvider so the same footer can drop into every page on the platform.
 */
export default function ComplianceFooter({ isDark: isDarkProp }) {
    const { theme } = useTheme();
    const isDark = typeof isDarkProp === "boolean" ? isDarkProp : theme === "dark";
    const auth = useAuth();
    const lang = resolveLang(auth?.user?.preferred_language || auth?.user?.user_locale);
    const copy = DISCLAIMER[lang];
    const isRtl = lang === "AR";
    const bg  = isDark ? "bg-[#090D16]" : "bg-white";
    const text= isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
    const brd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";

    return (
        <footer
            className={`${bg} ${text} border-t ${brd} pt-14 pb-8`}
            data-testid="compliance-footer"
        >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                <div className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_1.2fr_2.2fr] gap-8 md:gap-10 lg:gap-12">

                    {/* Col 1 · Product */}
                    <FooterCol title="Product">
                        <FooterAnchor target="hero-mdx"                label="Home" testid="footer-home" />
                        <FooterAnchor target="metrics-matrix"          label="How We Help You Trade Better" testid="footer-metrics" />
                        <FooterAnchor target="ai-copilot-comparison"   label="Your AI Helper"      testid="footer-copilot" />
                        <FooterAnchor target="exchange-footprint"      label="Markets We Cover"    testid="footer-coverage" />
                        <FooterAnchor target="pricing-mdx"             label="Pricing"            testid="footer-pricing" />
                    </FooterCol>

                    {/* Col 2 · Company */}
                    <FooterCol title="Company">
                        <FooterLink href="/about"   label="About"   testid="footer-about" />
                        <FooterLink href="/faq"     label="FAQ"     testid="footer-faq" />
                        <FooterLink href="/contact" label="Contact" testid="footer-contact" />
                        <FooterLink href="/login"   label="Sign In" testid="footer-signin" />
                        <FooterLink href="/register?intent=obondo_trial" label="Start Free Trial" testid="footer-trial" strong />
                    </FooterCol>

                    {/* Col 3 · Compliance Documents */}
                    <FooterCol title="Compliance Documents">
                        <FooterLink href="/legal/terms"           label="Terms of Service"                testid="footer-legal-terms" />
                        <FooterLink href="/legal/privacy"         label="Privacy Policy"                  testid="footer-legal-privacy" />
                        <FooterLink href="/legal/cookies"         label="Cookie Policy"                   testid="footer-legal-cookies" />
                        <FooterLink href="/legal/disclaimer"      label="Disclaimer & Limitation of Liability" testid="footer-legal-disclaimer" />
                        <FooterLink href="/legal/risk-disclosure" label="Risk Disclosure"                 testid="footer-legal-risk" />
                    </FooterCol>

                    {/* Col 4 · Corporate Info & localised compliance disclaimer */}
                    <div className="col-span-2 md:col-span-1" data-testid="footer-corporate" dir={isRtl ? "rtl" : undefined}>
                        <div
                            className="font-inter text-[11px] font-bold uppercase tracking-widest mb-3"
                            style={{ color: MDX.blueLow }}
                            data-testid="footer-corporate-heading"
                            data-lang={lang}
                        >
                            {copy.heading}
                        </div>
                        <div className={`font-roboto text-[13px] leading-relaxed ${dim} space-y-3`} data-testid="compliance-copy">
                            <p data-testid="compliance-body">{copy.body}</p>
                            <p data-testid="compliance-beco">{copy.beco}</p>
                        </div>
                    </div>
                </div>

                {/* Bottom rail — wordmark + copyright + language chips */}
                <div className={`mt-10 pt-6 border-t ${brd} flex flex-col md:flex-row md:items-center md:justify-between gap-4 font-roboto text-[12px] ${dim}`}>
                    <div className="flex items-center gap-3">
                        <span className="font-inter font-bold" style={{ color: MDX.blueLow }}>Melian&nbsp;Dialogue</span>
                        <span>© {new Date().getFullYear()} Melian Dialogue Ltd. All rights reserved.</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid="footer-langs">
                        {LANGS.map((l) => (
                            <span
                                key={l.code}
                                className={`px-1.5 py-0.5 rounded font-mono text-[10px] proportional-nums ${brd} border`}
                                title={l.label}
                            >
                                {l.code}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
}

/* ── column ─────────────────────────────────────────── */
function FooterCol({ title, children }) {
    return (
        <div>
            <div className="font-inter text-[11px] font-bold uppercase tracking-widest mb-4" style={{ color: "#922529" }}>
                {title}
            </div>
            <ul className="space-y-2">{children}</ul>
        </div>
    );
}

function FooterAnchor({ target, label, testid }) {
    const location = useLocation();
    const navigate = useNavigate();
    const scroll = (e) => {
        e.preventDefault();
        if (location.pathname === "/") {
            document.querySelector(`[data-testid=${target}]`)?.scrollIntoView({ behavior: "smooth" });
        } else {
            navigate(`/#${target}`);
        }
    };
    return (
        <li>
            <a
                href={`/#${target}`}
                onClick={scroll}
                data-testid={testid}
                className="font-roboto text-[14px] transition-colors"
                onMouseEnter={(e) => (e.currentTarget.style.color = "#922529")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "")}
            >
                {label}
            </a>
        </li>
    );
}

function FooterLink({ href, label, testid, strong }) {
    return (
        <li>
            <a
                href={href}
                data-testid={testid}
                className={`font-roboto text-[14px] transition-colors ${strong ? "font-semibold" : ""}`}
                style={strong ? { color: "var(--brand-green-fg, #006600)" } : {}}
                onMouseEnter={(e) => { if (!strong) e.currentTarget.style.color = "#922529"; }}
                onMouseLeave={(e) => { if (!strong) e.currentTarget.style.color = ""; }}
            >
                {label}
            </a>
        </li>
    );
}
